/**
 * Login Page
 * 
 * Comprehensive login interface with authentication integration
 * Connects to backend PostgreSQL auth system
 * 
 * Features:
 * - Email/password authentication
 * - Tenant ID support for multi-tenancy
 * - Form validation and error handling
 * - Responsive design with loading states
 * - Social login placeholders (future enhancement)
 * - "Remember Me" functionality
 */

'use client';

import { useState, useEffect, Suspense } from 'react';
import { useAuth } from '@/lib/hooks/useAuth';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/auth-store';
import { isValidEmail } from '@/lib/api/auth';
import { detectTenantSubdomain, type TenantHost } from '@/lib/tenant-subdomain';
import { Shield, Eye, EyeOff, AlertCircle, Loader2, X, Building2 } from 'lucide-react';
import { logger } from '@/lib/logger';

// ============================================================================
// LEGAL CONTENT
// ============================================================================

const TERMS_OF_SERVICE = [
  { title: '1. Acceptance of Terms', body: 'By accessing or using the UZO AI Platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of an organization, you agree to these Terms on behalf of that organization. If you do not agree to these Terms, do not use the Service.' },
  { title: '2. Description of Service', body: 'UZO provides an enterprise AI platform for building, deploying, and managing AI agents, RAG pipelines, workflow automations, and GenAI applications. The Service includes visual workflow builders, model integrations, MCP server connections, vector database management, guardrails, human-in-the-loop approvals, and observability tools.' },
  { title: '3. Account Registration & Security', body: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your credentials, including your organization code, email, and password. You must notify UZO immediately of any unauthorized use of your account. UZO is not liable for any loss arising from unauthorized access to your account.' },
  { title: '4. Multi-Tenancy & Data Isolation', body: 'UZO operates a multi-tenant architecture. Each organization\'s data is logically isolated using tenant identifiers. You may not attempt to access, modify, or interfere with another tenant\'s data, workflows, or configurations. Violation of tenant boundaries constitutes a material breach of these Terms.' },
  { title: '5. Acceptable Use', body: 'You agree not to: (a) use the Service to develop AI agents or workflows that violate any applicable law or regulation; (b) attempt to reverse-engineer, decompile, or extract source code from the Service; (c) use the Service to generate, store, or transmit malicious content, malware, or prompt injection attacks; (d) circumvent guardrails, rate limits, or safety mechanisms built into the platform; (e) share API keys, connectors, or credentials with unauthorized parties.' },
  { title: '6. AI Model Usage & Third-Party Services', body: 'The Service integrates with third-party AI model providers (OpenAI, Anthropic, Google, Azure, etc.) and external services via MCP servers and connectors. Your use of these integrations is subject to the respective provider\'s terms and usage policies. UZO is not responsible for the output, accuracy, or availability of third-party AI models. You are responsible for ensuring your use of AI models complies with applicable laws and the model providers\' acceptable use policies.' },
  { title: '7. Data Ownership & Processing', body: 'You retain all rights to your data, including workflows, configurations, prompts, documents, and outputs. UZO processes your data solely to provide the Service. UZO does not use your data to train AI models or share it with third parties except as required to deliver the Service (e.g., sending prompts to your configured AI model provider). Workflow execution data, including node run logs and observability metrics, is stored for operational purposes and is accessible only to your organization.' },
  { title: '8. Guardrails & Safety', body: 'UZO provides configurable guardrails including PII detection, prompt injection defense, content moderation, hallucination checks, and topic restrictions. These guardrails are tools to assist with safety and compliance but are not guaranteed to catch all violations. You are ultimately responsible for the safety, accuracy, and appropriateness of content generated through your AI agents and workflows.' },
  { title: '9. Human-in-the-Loop Approvals', body: 'The HITL approval system provides human oversight for AI agent actions. Approval timeouts, auto-approve settings, and escalation policies are configurable by your organization. UZO is not liable for decisions made through the approval system, whether approved, rejected, or auto-approved due to timeout.' },
  { title: '10. Service Availability & SLA', body: 'UZO strives to maintain high availability but does not guarantee uninterrupted service. Scheduled maintenance windows will be communicated in advance. UZO is not liable for downtime caused by third-party service outages (AI providers, cloud infrastructure, database services), force majeure events, or actions taken by your organization.' },
  { title: '11. Fees & Billing', body: 'Service fees are based on your subscription plan and usage. AI model inference costs are passed through from the respective providers. You are responsible for monitoring your usage and associated costs. UZO reserves the right to modify pricing with 30 days\' written notice.' },
  { title: '12. Intellectual Property', body: 'UZO and its licensors own all rights in the Service, including the platform, workflow engine, node registry, MCP framework, and documentation. Your use of the Service does not grant you any ownership rights in the Service. Workflow templates shared through the public template library are subject to the template author\'s specified license.' },
  { title: '13. Termination', body: 'Either party may terminate the agreement with 30 days\' written notice. UZO may suspend or terminate your access immediately if you breach these Terms. Upon termination, you may export your data within 30 days. After the export period, UZO will delete your data in accordance with the Privacy Policy.' },
  { title: '14. Limitation of Liability', body: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, UZO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR USE OF THE SERVICE. UZO\'S TOTAL LIABILITY SHALL NOT EXCEED THE FEES PAID BY YOU IN THE 12 MONTHS PRECEDING THE CLAIM.' },
  { title: '15. Governing Law', body: 'These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles. Any disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.' },
  { title: '16. Changes to Terms', body: 'UZO may update these Terms from time to time. Material changes will be communicated via email or in-app notification at least 30 days before taking effect. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.' },
];

const PRIVACY_POLICY = [
  { title: '1. Introduction', body: 'UZO Technologies, Inc. ("UZO", "we", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and share information when you use the UZO AI Platform ("Service"). This policy applies to all users, including administrators, developers, and end users within your organization.' },
  { title: '2. Information We Collect', body: 'Account Information: Name, email address, organization code, role, and authentication credentials. Usage Data: Workflow execution logs, node run details, API call metadata, feature usage analytics, and session information. Configuration Data: Workflow definitions, agent configurations, connector settings, guardrail rules, and MCP server configurations. AI Interaction Data: Prompts sent to AI models, model responses, token usage, and cost data — processed solely to deliver the Service. Technical Data: IP addresses, browser type, device information, and access timestamps for security and audit purposes.' },
  { title: '3. How We Use Your Information', body: 'We use your information to: (a) Provide, maintain, and improve the Service; (b) Authenticate users and enforce multi-tenant data isolation; (c) Process workflow executions and AI model requests; (d) Generate observability metrics, cost tracking, and audit logs; (e) Send service notifications, security alerts, and HITL approval requests; (f) Detect and prevent fraud, abuse, and security threats; (g) Comply with legal obligations and respond to lawful requests.' },
  { title: '4. Data Processing & AI Models', body: 'When you configure AI agents and workflows, prompts and data are sent to your selected AI model providers (e.g., OpenAI, Anthropic, Google). UZO acts as a data processor, transmitting your data to these providers on your behalf. We do not use your prompts, documents, or AI outputs to train any models. Each provider processes data according to their own privacy policy and data processing terms. We recommend reviewing the privacy practices of any AI providers you integrate.' },
  { title: '5. Guardrails & PII Processing', body: 'When guardrails are enabled, content may be scanned for PII (personally identifiable information), prompt injection attempts, toxicity, and other safety concerns. PII detection operates locally within the platform and does not send sensitive data to external services unless explicitly configured. Detected PII can be redacted, masked, hashed, or blocked based on your configuration. Guardrail scan results are logged for audit purposes but detected PII content is not stored in plain text after redaction.' },
  { title: '6. Data Storage & Security', body: 'Data is stored in PostgreSQL databases with tenant-level logical isolation. All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Authentication uses secure HttpOnly cookies — tokens are not stored in browser localStorage. Access controls enforce role-based permissions within each tenant. Database backups are encrypted and retained per your subscription plan\'s retention policy. Vector database embeddings are stored in your configured vector store (pgvector, Qdrant, Pinecone, or ChromaDB).' },
  { title: '7. Data Retention', body: 'Workflow execution logs and node run details are retained for the duration specified in your organization\'s settings. Chat session data is retained based on your configured session TTL (default: 7 days). Audit logs are retained for 1 year minimum for compliance purposes. Upon account termination, all data is deleted within 30 days of the export period, except where retention is required by law.' },
  { title: '8. Data Sharing', body: 'We do not sell your personal information. We share data only with: AI Model Providers — prompts and context sent to your configured providers for inference; Infrastructure Providers — cloud hosting providers under strict data processing agreements; Legal Compliance — when required by law, court order, or government request; Business Transfers — in connection with a merger, acquisition, or sale of assets, with prior notice to affected users.' },
  { title: '9. Your Rights', body: 'Depending on your jurisdiction, you may have the right to: Access, correct, or delete your personal data; Export your data in a portable format; Object to or restrict certain processing; Withdraw consent where processing is based on consent; Lodge a complaint with a data protection authority. To exercise these rights, contact your organization administrator or email privacy@uzo.ai.' },
  { title: '10. International Data Transfers', body: 'If you are located outside the United States, your data may be transferred to and processed in the United States where our servers are located. We implement appropriate safeguards for international transfers, including Standard Contractual Clauses (SCCs) where required by applicable law.' },
  { title: '11. Cookies & Tracking', body: 'We use essential cookies for authentication and session management. We do not use advertising cookies or third-party tracking pixels. Analytics cookies may be used to understand feature usage and improve the Service, and can be disabled in your browser settings.' },
  { title: '12. Children\'s Privacy', body: 'The Service is not directed to individuals under 16 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will delete it promptly.' },
  { title: '13. Changes to This Policy', body: 'We may update this Privacy Policy periodically. Material changes will be communicated via email or in-app notification at least 30 days before taking effect. The "Last Updated" date at the top of this policy indicates when it was last revised.' },
  { title: '14. Contact Us', body: 'If you have questions about this Privacy Policy or our data practices, contact us at: UZO Technologies, Inc., Email: privacy@uzo.ai, Address: 251 Little Falls Drive, Wilmington, DE 19808, United States.' },
];

// ============================================================================
// LEGAL MODAL COMPONENT
// ============================================================================

function LegalModal({
  isOpen,
  onClose,
  title,
  sections,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  sections: { title: string; body: string }[];
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div role="presentation" className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative flex w-full max-w-2xl max-h-[80vh] flex-col rounded-xl border border-uzo-line bg-uzo-surface shadow-uzo-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-uzo-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-uzo-accent">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <h2 className="text-lg font-bold text-uzo-ink">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-uzo-mut transition-colors hover:bg-uzo-bg"
          aria-label="Close">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Effective date */}
        <div className="bg-uzo-bg px-6 py-2 text-xs text-uzo-mut">
          Last Updated: March 1, 2026 &middot; Effective: March 1, 2026
        </div>

        {/* Content */}
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
          {sections.map((section, i) => (
            <div key={i}>
              <h3 className="mb-1.5 text-sm font-semibold text-uzo-ink">
                {section.title}
              </h3>
              <p className="text-sm leading-relaxed text-uzo-ink2">
                {section.body}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-uzo-line px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-full bg-uzo-accent px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-uzo-accent-ink"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LOGIN FORM COMPONENT
// ============================================================================

function LoginForm() {
  // State management
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    tenant_code: '', // Organization code (e.g., ACME)
    rememberMe: false
  });

  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
    tenant_code: '',
    general: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [ipInfo, setIpInfo] = useState<any>({});
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);
  const [themeBg, setThemeBg] = useState('#F5F0EB');
  const [themeIsDark, setThemeIsDark] = useState(false);
  // Capture where to send the user after login (set from ?redirect= param)
  const [postLoginRedirect, setPostLoginRedirect] = useState('/chat');
  // Tenant subdomain detection: when present, hide the org-code field — backend
  // resolves tenant from Host header. The subdomain is the source of truth; users
  // on a tenant subdomain cannot pick a different org. Manual entry is only used
  // when there's no subdomain (bare apex, localhost).
  const [tenantHost, setTenantHost] = useState<TenantHost | null>(null);

  // Auth hooks
  const { login, isLoading, isAuthenticated } = useAuth();
  const router = useRouter();

  // ========================================================================
  // EFFECTS
  // ========================================================================

  // Session message to display
  const [sessionMessage, setSessionMessage] = useState<{ type: 'info' | 'warning'; text: string } | null>(null);

  /**
   * Handle session-related URL parameters and clear stale auth data
   * This provides clear UX messaging about why the user needs to log in
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    const fromLogout = params.get('logout') === '1';
    // `redirect` param is set by middleware when it redirects an unauthenticated
    // user from any protected route to /login (e.g. /login?redirect=/workflows).
    // Its presence means the session expired — we must clear stale auth state or
    // the 100ms "already authenticated" timer below will fire and send the user
    // straight to /chat instead of showing the login form.
    const redirectTarget = params.get('redirect');
    const fromRedirect = !!redirectTarget;

    // Remember where to send the user after successful login
    if (redirectTarget) {
      setPostLoginRedirect(redirectTarget);
    }

    // Determine session message based on reason
    if (reason === 'session_expired') {
      setSessionMessage({
        type: 'warning',
        text: 'Your session has expired. Please sign in again.'
      });
    } else if (reason === 'session_refresh') {
      setSessionMessage({
        type: 'info',
        text: 'For security, please sign in again to continue.'
      });
    } else if (reason === 'unauthorized') {
      setSessionMessage({
        type: 'warning',
        text: 'You need to sign in to access that page.'
      });
    } else if (fromLogout) {
      setSessionMessage({
        type: 'info',
        text: 'You have been signed out successfully.'
      });
    } else if (fromRedirect) {
      setSessionMessage({
        type: 'warning',
        text: 'Your session has expired. Please sign in again.'
      });
    }

    // If we arrived at login page from a protected route (any mechanism), clear
    // stale auth data so the login form is shown rather than a redirect to /chat.
    if (fromLogout || reason || fromRedirect || document.referrer.includes('/workspaces') || document.referrer.includes('/projects')) {
      logger.debug('🧹 [LoginPage] Clearing stale auth data (redirected from protected page)');
      localStorage.removeItem('auth-storage');
      // Also clear Zustand store
      useAuthStore.getState().logout();
    }

    // Clean up URL parameters after reading them
    if (reason || fromLogout || fromRedirect) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  /**
   * Redirect if already authenticated (only if we have a valid session)
   * Skip redirect if we just cleared stale data
   */
  useEffect(() => {
    // Small delay to let the cleanup effect run first
    const timer = setTimeout(() => {
      const { isAuthenticated: stillAuth, user: authUser } = useAuthStore.getState();
      if (stillAuth) {
        // Forced password change wins over any other destination.
        if ((authUser as any)?.must_change_password) {
          router.replace('/change-password');
          return;
        }
        // Respect the original intent — if the middleware sent the user
        // here from `/workflows` etc. via `?redirect=…`, take them back
        // there instead of always landing on /chat. Without this, the
        // 2026-05-20 audit's "every protected route bounces to /chat"
        // pattern still surfaces even when the middleware (transiently)
        // 302s a route through /login.
        //
        // The redirect target is validated to be a same-origin path so
        // a malicious `?redirect=//evil.com/...` can't open-redirect.
        const params = new URLSearchParams(window.location.search);
        const redirectTarget = params.get('redirect');
        const isSafeInternalPath = (p: string | null) =>
          typeof p === 'string' && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/\\');
        const destination = isSafeInternalPath(redirectTarget) ? redirectTarget! : '/chat';
        logger.debug('🎯 [LoginPage] User already authenticated, redirecting to:', destination);
        router.replace(destination);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [router]);

  /**
   * Fetch IP information for audit logging
   */
  useEffect(() => {
    const fetchIpInfo = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('https://ipapi.co/json/', { 
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        const data = await response.json();
        setIpInfo(data);
        logger.debug('📍 [LoginPage] IP info fetched:', data.city, data.country_name);
      } catch (error) {
        logger.warn('⚠️  [LoginPage] Failed to fetch IP info:', error);
        setIpInfo({});
      }
    };

    fetchIpInfo();
  }, []);

  /**
   * Sync background color with landing page theme
   */
  useEffect(() => {
    try {
      const saved = localStorage.getItem('nue-site-theme');
      if (saved) {
        const theme = JSON.parse(saved);
        if (theme.bg) setThemeBg(theme.bg);
        if (theme.isDark) setThemeIsDark(theme.isDark);
      }
    } catch {}
  }, []);

  // Detect tenant subdomain (e.g. acme.dataver.co) so we can hide the org-code field.
  useEffect(() => {
    setTenantHost(detectTenantSubdomain());
  }, []);

  // ========================================================================
  // FORM HANDLING
  // ========================================================================

  /**
   * Handle form input changes
   */
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear specific field error when user starts typing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const errors = {
      email: '',
      password: '',
      tenant_code: '',
      general: ''
    };

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }

    // Tenant Code validation — skipped when accessing via tenant subdomain
    // (backend resolves tenant from Host header).
    const subdomainMode = !!tenantHost;
    if (!subdomainMode) {
      if (!formData.tenant_code) {
        errors.tenant_code = 'Organization code is required';
      } else if (formData.tenant_code.length < 2) {
        errors.tenant_code = 'Organization code must be at least 2 characters';
      }
    }

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    logger.debug('📝 [LoginPage] Form submission started for:', formData.email);

    // Clear general error
    setFormErrors(prev => ({ ...prev, general: '' }));

    // Validate form
    if (!validateForm()) {
      logger.warn('⚠️  [LoginPage] Form validation failed');
      return;
    }

    try {
      // Prepare login credentials
      // When accessing via tenant subdomain (e.g. acme.dataver.co), omit tenant_code
      // so the backend resolves tenant from the Host header. Empty string is also
      // treated as "absent" by the backend (`if login_request.tenant_code:` check).
      const subdomainMode = !!tenantHost;
      const credentials = {
        emailId: formData.email,
        password: formData.password,
        ...(subdomainMode ? {} : { tenant_code: formData.tenant_code }),
        rememberMe: formData.rememberMe, // Request extended session from backend
        ipInfo: ipInfo
      };

      // Attempt login — pass postLoginRedirect so useAuth navigates to the
      // page the user was trying to reach before the session expired
      await login(credentials, postLoginRedirect);

      logger.debug('✅ [LoginPage] Login successful');
      
    } catch (error: any) {
      logger.error('❌ [LoginPage] Login failed:', error.message);
      
      // Set general error message
      setFormErrors(prev => ({ 
        ...prev, 
        general: error.message || 'Login failed. Please check your credentials and try again.' 
      }));
    }
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <main className="flex items-center justify-center min-h-screen bg-uzo-bg p-6">
      <div className="w-full max-w-md">
        {/* Logo — click to go back to landing page */}
        <a href="/" className="flex items-center justify-center gap-3 mb-8 transition-opacity cursor-pointer hover:opacity-80">
          {/* The same mark, at the same size, as the top of the sidebar. */}
          <img src="/icon.svg?v=2" alt="" width={23} height={26} className="h-[26px] w-auto shrink-0" />
          <h1 className="text-2xl font-bold tracking-[-.02em] text-uzo-ink">
            UZO
          </h1>
        </a>

        <div className="rounded-xl border border-uzo-line bg-uzo-surface p-8 shadow-uzo-lg md:p-10">
          {/* Session Message */}
          {sessionMessage && (
            <div className={`mb-6 flex items-start gap-3 rounded-lg border p-4 ${
              sessionMessage.type === 'warning'
                ? 'border-uzo-amber-line bg-uzo-amber-soft text-uzo-amber'
                : 'border-uzo-line bg-uzo-cyan-soft text-uzo-cyan'
            }`}>
              {sessionMessage.type === 'warning' ? (
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              ) : (
                <Shield className="h-5 w-5 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="text-sm font-medium">{sessionMessage.text}</p>
                <p className="mt-1 text-xs opacity-80">
                  Your data is protected. Tokens are not stored in your browser.
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <h2 className="mb-2 text-3xl font-bold text-uzo-ink">Welcome back</h2>
            <p className="text-uzo-ink2">Sign in to continue to your workspace</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block mb-2 text-sm font-medium text-uzo-ink2">
                Email address <span className="text-uzo-red-ink">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`w-full rounded-lg border px-4 py-3 text-uzo-ink outline-none transition-colors placeholder:text-uzo-faint focus:border-uzo-accent focus:ring-2 focus:ring-uzo-accent-soft ${
                  formErrors.email ? 'border-uzo-red bg-uzo-red-soft' : 'border-uzo-line'
                }`}
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={isLoading}
              />
              {formErrors.email && (
                <p className="flex items-center gap-1 mt-2 text-sm text-uzo-red-ink">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block mb-2 text-sm font-medium text-uzo-ink2">
                Password <span className="text-uzo-red-ink">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className={`w-full rounded-lg border px-4 py-3 pr-12 text-uzo-ink outline-none transition-colors placeholder:text-uzo-faint focus:border-uzo-accent focus:ring-2 focus:ring-uzo-accent-soft ${
                    formErrors.password ? 'border-uzo-red bg-uzo-red-soft' : 'border-uzo-line'
                  }`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-uzo-faint" aria-hidden="true" />
                  ) : (
                    <Eye className="w-5 h-5 text-uzo-faint" aria-hidden="true" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="flex items-center gap-1 mt-2 text-sm text-uzo-red-ink">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.password}
                </p>
              )}
            </div>

            {/* Organization — locked to subdomain when present, otherwise manually entered */}
            {tenantHost ? (
              <div className="flex items-start gap-3 rounded-lg border border-uzo-line bg-uzo-bg px-4 py-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-uzo-accent">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-uzo-mut">Signing in to</p>
                  <p className="text-sm font-semibold uppercase truncate text-uzo-ink">
                    {tenantHost.subdomain}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="tenant_code" className="block mb-2 text-sm font-medium text-uzo-ink2">
                  Organization Code <span className="text-uzo-red-ink">*</span>
                </label>
                <input
                  id="tenant_code"
                  name="tenant_code"
                  type="text"
                  autoComplete="organization"
                  required
                  className={`w-full rounded-lg border px-4 py-3 uppercase text-uzo-ink outline-none transition-colors placeholder:text-uzo-faint focus:border-uzo-accent focus:ring-2 focus:ring-uzo-accent-soft ${
                    formErrors.tenant_code ? 'border-uzo-red bg-uzo-red-soft' : 'border-uzo-line'
                  }`}
                  placeholder=""
                  value={formData.tenant_code}
                  onChange={(e) => handleInputChange('tenant_code', e.target.value.toUpperCase())}
                  disabled={isLoading}
                />
                {formErrors.tenant_code && (
                  <p className="flex items-center gap-1 mt-2 text-sm text-uzo-red-ink">
                    <AlertCircle className="w-4 h-4" />
                    {formErrors.tenant_code}
                  </p>
                )}
                <p className="mt-1 text-xs text-uzo-mut">
                  Your organization code (provided by your administrator)
                </p>
              </div>
            )}

            {/* General Error Message */}
            {formErrors.general && (
              <div className="flex items-start gap-2 rounded-lg border border-uzo-line bg-uzo-red-soft px-4 py-3 text-sm text-uzo-red-ink">
                <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Login Failed</p>
                  <p>{formErrors.general}</p>
                </div>
              </div>
            )}

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="w-4 h-4 cursor-pointer rounded border-uzo-line accent-uzo-accent"
                  checked={formData.rememberMe}
                  onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                  disabled={isLoading}
                />
                <label htmlFor="remember-me" className="block ml-2 text-sm cursor-pointer text-uzo-ink2">
                  Keep me signed in
                </label>
              </div>

              <div className="text-sm">
                <a
                  href="/login/reset"
                  className="font-medium text-uzo-accent transition-colors hover:text-uzo-accent-ink"
                >
                  Forgot password?
                </a>
              </div>
            </div>

            {/* Security Note */}
            <div className="rounded-lg bg-uzo-bg p-3 text-xs text-uzo-mut">
              <p className="flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                <span>
                  {formData.rememberMe
                    ? 'Extended session via secure HttpOnly cookie. You may need to re-authenticate after closing all browser tabs.'
                    : 'Session ends when you close all browser tabs or after 1 hour of inactivity.'}
                </span>
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center rounded-full border border-transparent bg-uzo-accent px-4 py-3 text-[15px] font-semibold tracking-[-.01em] text-white transition-colors hover:bg-uzo-accent-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Terms */}
          <div className="mt-8 text-center">
            <p className="text-xs text-uzo-mut">
              By signing in, you agree to our{' '}
              <button
                type="button"
                onClick={() => setLegalModal('terms')}
                className="text-uzo-accent underline hover:text-uzo-accent-ink"
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => setLegalModal('privacy')}
                className="text-uzo-accent underline hover:text-uzo-accent-ink"
              >
                Privacy Policy
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Legal Modals */}
      <LegalModal
        isOpen={legalModal === 'terms'}
        onClose={() => setLegalModal(null)}
        title="Terms of Service"
        sections={TERMS_OF_SERVICE}
      />
      <LegalModal
        isOpen={legalModal === 'privacy'}
        onClose={() => setLegalModal(null)}
        title="Privacy Policy"
        sections={PRIVACY_POLICY}
      />
    </main>
  );
}

// ============================================================================
// MAIN LOGIN PAGE COMPONENT
// ============================================================================

function LoginPageWithSuspense() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-uzo-bg">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-uzo-accent" />
          <p className="text-uzo-ink2">Loading authentication...</p>
        </div>
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}

export default LoginPageWithSuspense;
